import React from 'react';

import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Button,
  Icon,
  Badge,
  SimpleGrid,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { FaFilter } from 'react-icons/fa';

import { useColors } from './colors';

const FIELD_OPTIONS = [
  { value: 'all', label: 'All fields' },
  { value: 'supplierIdentifier', label: 'Supplier SKU' },
  { value: 'partslink', label: 'Partslink' },
  { value: 'oem', label: 'OEM' },
  { value: 'description', label: 'Description' },
  { value: 'brand', label: 'Brand' },
];

const SearchToolbar = ({
  searchQuery,
  searchField,
  selectedSupplier,
  supplierOptions,
  suppliersLoading,
  isSearching,
  onSearchQueryChange,
  onSearchFieldChange,
  onSupplierChange,
  onSearch,
  onKeyDown,
}) => {
  const {
    backgroundCardColor,
    textColor,
    secondaryTextColor,
    borderColor,
    inputBackground,
    accentColor,
    placeholderColor,
  } = useColors();

  return (
    <Box
      p={{ base: 5, md: 6 }}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="2xl"
      bg={backgroundCardColor}
      boxShadow="sm"
    >
      <VStack align="stretch" spacing={5}>
        <Flex
          direction={{ base: 'column', md: 'row' }}
          justify="space-between"
          align={{ base: 'flex-start', md: 'center' }}
          gap={3}
        >
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color={textColor}>
              Inventory Search
            </Text>
            <Text fontSize="sm" color={secondaryTextColor} mt={1}>
              Search the latest chunked inventory file per supplier
            </Text>
          </Box>
          <Badge
            colorScheme="blue"
            variant="subtle"
            px={3}
            py={1}
            borderRadius="full"
            fontSize="xs"
            textTransform="none"
          >
            Live inventory data
          </Badge>
        </Flex>

        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
          <Box gridColumn={{ lg: 'span 2' }}>
            <Text fontSize="xs" fontWeight="semibold" color={secondaryTextColor} mb={2}>
              SEARCH TERM
            </Text>
            <InputGroup size="lg">
              <InputLeftElement pointerEvents="none" h="full">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="SKU, partslink, OEM, description…"
                bg={inputBackground}
                color={textColor}
                borderColor={borderColor}
                borderRadius="xl"
                pl={10}
                _placeholder={{ color: placeholderColor }}
                _focus={{ borderColor: accentColor, boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
              />
            </InputGroup>
          </Box>

          <Box>
            <Text fontSize="xs" fontWeight="semibold" color={secondaryTextColor} mb={2}>
              SUPPLIER
            </Text>
            <Select
              size="lg"
              value={selectedSupplier}
              onChange={(e) => onSupplierChange(e.target.value)}
              isDisabled={suppliersLoading}
              bg={inputBackground}
              color={textColor}
              borderColor={borderColor}
              borderRadius="xl"
              icon={<FaFilter />}
              placeholder="All suppliers"
            >
              <option value="">All suppliers</option>
              {supplierOptions.map((option) => (
                <option key={option.id} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Box>
        </SimpleGrid>

        <Flex
          direction={{ base: 'column', sm: 'row' }}
          gap={3}
          align={{ base: 'stretch', sm: 'flex-end' }}
          justify="space-between"
        >
          <Box flex={1} maxW={{ sm: '280px' }}>
            <Text fontSize="xs" fontWeight="semibold" color={secondaryTextColor} mb={2}>
              SEARCH IN FIELD
            </Text>
            <Select
              size="md"
              value={searchField}
              onChange={(e) => onSearchFieldChange(e.target.value)}
              bg={inputBackground}
              color={textColor}
              borderColor={borderColor}
              borderRadius="xl"
            >
              {FIELD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Box>

          <HStack spacing={3} justify="flex-end">
            <Button
              size="lg"
              colorScheme="blue"
              leftIcon={<Icon as={SearchIcon} />}
              onClick={() => onSearch()}
              isLoading={isSearching}
              loadingText="Searching"
              borderRadius="xl"
              px={8}
            >
              Search
            </Button>
          </HStack>
        </Flex>
      </VStack>
    </Box>
  );
};

export default SearchToolbar;
